import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import Toast from './Toast';
import styles from '../styles/ToastContainer.module.css';

// Create a toast container
let toastContainer;

// Function to get or create a toast container
const getContainer = () => {
  if (!toastContainer) {
    toastContainer = document.createElement('div');
    toastContainer.className = styles.toastContainer;
    document.body.appendChild(toastContainer);
  }
  return toastContainer;
};

/**
 * Toast container component - manages and renders multiple toast notifications
 * 
 * @returns {JSX.Element}
 */
const ToastContainer = () => {
  const [toasts, setToasts] = useState([]);
  
  // Function to remove a toast by its ID
  const removeToast = useCallback((id) => {
    setToasts(currentToasts => currentToasts.filter(toast => toast.id !== id));
  }, []);
  
  // Create a global function to add toasts
  useEffect(() => {
    window.showToast = (type, message, duration = 3000) => {
      const id = Date.now();
      setToasts(currentToasts => [...currentToasts, { id, type, message, duration }]);
      
      // Return the ID so it can be used to remove the toast
      return id;
    };
    
    window.removeToast = (id) => {
      removeToast(id);
    };
    
    return () => {
      // Clean up the global functions when the component unmounts
      window.showToast = undefined;
      window.removeToast = undefined;
    };
  }, [removeToast]);
  
  return ReactDOM.createPortal(
    <>
      {toasts.map(toast => (
        <Toast
          key={toast.id}
          type={toast.type}
          message={toast.message}
          duration={toast.duration}
          onClose={() => removeToast(toast.id)}
        />
      ))}
    </>,
    getContainer()
  );
};

// Export functions for showing toasts
export const showToast = (type, message, duration = 3000) => {
  if (window.showToast) {
    return window.showToast(type, message, duration);
  }
  return null;
};

export const removeToast = (id) => {
  if (window.removeToast) {
    window.removeToast(id);
  }
};

export default ToastContainer;
